
import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import { useSEO } from '../../utils/seo';

export default function PolitiqueConfidentialite() {
  useSEO({
    title: 'Politique de confidentialité — OTOROBOTS CANADA INC.',
    description: 'Politique de confidentialité d\'OTOROBOTS CANADA INC. : collecte, utilisation et protection de vos données personnelles. Conformité aux lois canadiennes sur la protection des données.',
    keywords: 'politique de confidentialité, protection des données, LPRPDE, vie privée, OTOROBOTS, Canada',
    canonical: '/politique-confidentialite',
    ogTitle: 'Politique de confidentialité — OTOROBOTS CANADA INC.',
    ogDescription: 'Politique de confidentialité d\'OTOROBOTS CANADA INC. : collecte, utilisation et protection de vos données personnelles.',
    schema: {
      '@type': 'WebPage',
      name: 'Politique de confidentialité',
      description: 'Politique de confidentialité d\'OTOROBOTS CANADA INC.',
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <header>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-8">
              Politique de confidentialité
            </h1>
          </header>

          <article className="space-y-8 text-gray-700 leading-relaxed">
            <section>
              <p className="text-lg mb-4">
                OTOROBOTS CANADA INC. s'engage à protéger la confidentialité et la sécurité des informations 
                personnelles de ses visiteurs. Cette politique décrit nos pratiques concernant la collecte, 
                l'utilisation et la protection de vos données personnelles.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Collecte des données
              </h2>
              <p className="mb-3">
                OTOROBOTS CANADA INC. collecte uniquement les données personnelles que vous nous fournissez 
                volontairement par le biais du formulaire de contact présent sur ce site.
              </p>
              <p className="mb-3">
                Les informations collectées peuvent inclure :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Votre nom</li>
                <li>Votre adresse courriel</li>
                <li>Votre message ou demande</li>
              </ul>
              <p className="mt-3">
                Aucune donnée n'est collectée automatiquement lors de votre navigation sur le site, 
                à l'exception des données techniques standard (adresse IP, type de navigateur) nécessaires 
                au fonctionnement du site.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Utilisation des données
              </h2>
              <p className="mb-3">
                Les données personnelles collectées sont utilisées uniquement aux fins suivantes :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Répondre à vos demandes d'information</li>
                <li>Communiquer avec vous concernant nos activités</li>
                <li>Améliorer nos services et notre site web</li>
              </ul>
              <p className="mt-3">
                Vos données ne sont jamais utilisées à des fins commerciales non sollicitées.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Partage des données
              </h2>
              <p className="mb-3">
                OTOROBOTS CANADA INC. ne vend, ne loue et ne partage jamais vos données personnelles 
                avec des tiers à des fins commerciales.
              </p>
              <p>
                Vos informations peuvent être partagées uniquement dans les cas suivants :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>Avec votre consentement explicite</li>
                <li>Pour se conformer à une obligation légale</li>
                <li>Pour protéger les droits et la sécurité d'OTOROBOTS CANADA INC.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Sécurité des données
              </h2>
              <p>
                Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées 
                pour protéger vos données personnelles contre tout accès non autorisé, toute modification, 
                divulgation ou destruction. Les données sont hébergées de façon sécurisée et ne sont accessibles 
                qu'au personnel autorisé.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Services tiers
              </h2>
              <p>
                Bien que ce site web soit à vocation informative et n'effectue aucune transaction commerciale, 
                OTOROBOTS CANADA INC. peut utiliser des services tiers (tels que Stripe ou d'autres plateformes) 
                dans le cadre d'initiatives distinctes ou de projets spécifiques.
              </p>
              <p className="mt-3">
                Ces services tiers ont leurs propres politiques de confidentialité et ne sont pas directement 
                intégrés sur ce site institutionnel.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Vos droits
              </h2>
              <p className="mb-3">
                Conformément aux lois canadiennes applicables en matière de protection des données personnelles, 
                vous disposez des droits suivants :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Droit d'accès à vos données personnelles</li>
                <li>Droit de rectification de vos données</li>
                <li>Droit de suppression de vos données</li>
                <li>Droit de retirer votre consentement</li>
                <li>Droit de déposer une plainte auprès de l'autorité compétente</li>
              </ul>
              <p className="mt-3">
                Pour exercer ces droits, veuillez nous contacter à l'adresse : 
                <a href="mailto:info@otorobots.com" className="text-teal-600 hover:text-teal-700 font-medium ml-1">
                  info@otorobots.com
                </a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Conservation des données
              </h2>
              <p>
                Vos données personnelles sont conservées uniquement pendant la durée nécessaire aux finalités 
                pour lesquelles elles ont été collectées, ou conformément aux exigences légales applicables.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Modifications de la politique
              </h2>
              <p>
                OTOROBOTS CANADA INC. se réserve le droit de modifier cette politique de confidentialité à tout moment. 
                Toute modification sera publiée sur cette page avec une date de mise à jour. Nous vous encourageons 
                à consulter régulièrement cette page.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Conformité légale
              </h2>
              <p>
                Cette politique de confidentialité est conforme aux lois canadiennes applicables en matière 
                de protection des données personnelles, notamment la Loi sur la protection des renseignements 
                personnels et les documents électroniques (LPRPDE).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Contact
              </h2>
              <p>
                Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits, 
                vous pouvez nous contacter à :
              </p>
              <p className="mt-2">
                <a href="mailto:info@otorobots.com" className="text-teal-600 hover:text-teal-700 font-medium">
                  info@otorobots.com
                </a>
              </p>
            </section>

            <section className="pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Dernière mise à jour : Janvier 2026
              </p>
            </section>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}
